/** @typedef {{ value: string, hidden?: boolean, rowspan?: number, colspan?: number }} GoCell */
/** Layout for GoPricingSheetGrid / docx export. */

export const goPricingLayoutEngine = {
  layout(sheetRows, variant = 'default') {
      const rows = Array.isArray(sheetRows) ? sheetRows : []
      if (!rows.length) {
        return {
          special: false,
          metaRows: [],
          yearValue: '',
          valueHeaders: {
            value1: 'Значение 1',
            value2: 'Значение 2',
          },
          title: '',
          formTitle: '',
          note: '',
          emphasizeFirstRow: false,
          tableRows: rows,
        }
      }

      if (variant === 'procurement') {
        return goPricingLayoutEngine.layoutProcurement(sheetRows)
      }

      if (variant === 'pricing') {
        return goPricingLayoutEngine.layoutPricing(sheetRows)
      }

      const hasMetaKeys = rows.some(row =>
        goPricingLayoutEngine.rowValues(row).some(v => /^(МНН|ЛФ|ФВ)$/i.test(v))
      )
      const hasGoTitle = rows.some(row =>
        goPricingLayoutEngine.rowValues(row).some(v => /^выпуск в гражданский оборот/i.test(v))
      )
      const hasMonthHeader = rows.some(row =>
        goPricingLayoutEngine.rowValues(row).some(v => /^месяц$/i.test(v))
      )
      const hasYearFormHeader = rows.some(row => {
        const vals = goPricingLayoutEngine.rowValues(row)
        return vals.some(v => /^год$/i.test(v)) && vals.some(v => /^форма выпуска$/i.test(v))
      })

      if (!hasMetaKeys && !hasGoTitle && !hasMonthHeader && !hasYearFormHeader) {
        return {
          special: false,
          metaRows: [],
          yearValue: '',
          valueHeaders: {
            value1: 'Значение 1',
            value2: 'Значение 2',
          },
          title: '',
          formTitle: '',
          note: '',
          emphasizeFirstRow: false,
          tableRows: rows,
        }
      }

      const parsedRows = rows.map((row, idx) => ({ idx, values: goPricingLayoutEngine.rowValues(row) }))
      const metaRows = []
      let yearValue = ''
      let valueHeaders = { value1: 'Значение 1', value2: 'Значение 2' }
      let title = ''
      let note = ''
      let tableStart = 0
      let tableEnd = rows.length

      const goTitleIndex = parsedRows.findIndex(r =>
        r.values.some(v => /^выпуск в гражданский оборот/i.test(v))
      )

      for (const r of parsedRows) {
        const { idx: i, values } = r
        if (!values.length) continue
        const key = values[0]

        if (/^(МНН|ЛФ|ФВ)$/i.test(key)) {
          metaRows.push({
            key,
            value1: values[1] || '',
            value2: values[2] || '',
          })
          continue
        }
        if (values.some(v => /^значение\s*1$/i.test(v))) {
          const v1 = values.find(v => /^значение\s*1$/i.test(v))
          const v2 = values.find(v => /^значение\s*2$/i.test(v))
          valueHeaders = {
            value1: v1 || 'Значение 1',
            value2: v2 || 'Значение 2',
          }
          continue
        }
        if (/^год$/i.test(key) && i < (goTitleIndex >= 0 ? goTitleIndex : rows.length)) {
          yearValue = values.slice(1).join(' ').trim()
          continue
        }
      }

      const noteStartMain = goPricingLayoutEngine.findFirstNoteSectionRowIndex(rows, 0)
      if (noteStartMain >= 0) {
        tableEnd = noteStartMain
        note = goPricingLayoutEngine.collectNoteRows(rows, noteStartMain)
      }

      if (hasMonthHeader) {
        const normalizedPlan = goPricingLayoutEngine.normalizePlanSupplyTable(rows)
        if (normalizedPlan.tableRows.length) {
          return {
            special: true,
            metaRows: [],
            yearValue: '',
            valueHeaders: {
              value1: 'Значение 1',
              value2: 'Значение 2',
            },
            title: normalizedPlan.title,
            formTitle: normalizedPlan.formTitle,
            note: normalizedPlan.note,
            emphasizeFirstRow: true,
            tableRows: goPricingLayoutEngine.stripPlanTableYearColumn(
              goPricingLayoutEngine.groupYearCells(normalizedPlan.tableRows)
            ),
          }
        }
      }

      if (goTitleIndex >= 0 && hasMetaKeys) {
        title = 'Выпуск в гражданский оборот за календарный год'
        const foundStart = parsedRows.find(r => {
          if (r.idx <= goTitleIndex) return false
          const vals = r.values
          return (
            vals.some(v => /^форма выпуска$/i.test(v)) ||
            vals.some(v => /^год$/i.test(v)) ||
            vals.some(v => /^\d{4}\s*год$/i.test(v))
          )
        })
        tableStart = foundStart ? foundStart.idx : goTitleIndex + 1
        const normalized = goPricingLayoutEngine.normalizeGoTable(rows, tableStart, tableEnd)
        if (normalized.length) {
          return {
            special: true,
            metaRows,
            yearValue,
            valueHeaders,
            title,
            note,
            emphasizeFirstRow: false,
            tableRows: normalized,
          }
        }
      } else {
        const foundStart = parsedRows.find(r => {
          const vals = r.values
          return (
            vals.some(v => /^месяц$/i.test(v)) ||
            (vals.some(v => /^год$/i.test(v)) &&
              vals.some(v => /^форма выпуска$/i.test(v)))
          )
        })
        tableStart = foundStart ? foundStart.idx : 0
      }

      if (!metaRows.length && !yearValue && !title) {
        return {
          special: false,
          metaRows: [],
          yearValue: '',
          valueHeaders: {
            value1: 'Значение 1',
            value2: 'Значение 2',
          },
          title: '',
          formTitle: '',
          note: '',
          emphasizeFirstRow: false,
          tableRows: rows,
        }
      }

      return {
        special: true,
        metaRows,
        yearValue,
        valueHeaders,
        title,
        formTitle: '',
        note,
        emphasizeFirstRow: false,
        tableRows: rows.slice(tableStart, tableEnd),
      }
  },

    leadingColumnCellIsBlank(cell) {
      if (!cell || cell.hidden) return true
      const v = String(cell.value ?? '').trim()
      if (!v) return true
      if (v === '-' || v === '~') return true
      if (/^закупки$/i.test(v)) return true
      return false
    },
    stripLeadingEmptyColumns(tableRows) {
      let rows = (tableRows || []).map(r => [...(r || [])])
      if (!rows.length) return rows

      while (rows.some(r => r.length > 0)) {
        const hasContent = rows.some(
          row => row.length > 0 && !goPricingLayoutEngine.leadingColumnCellIsBlank(row[0])
        )
        if (hasContent) break
        rows = rows.map(row => row.slice(1))
      }
      return rows
    },
    emptyProcurementLayout(
      tableRows = [],
      note = '',
      { stripLeading = false, layoutVariant = '' } = {}
    ) {
      const trimmed = stripLeading
        ? goPricingLayoutEngine.stripLeadingEmptyColumns(tableRows)
        : tableRows
      return {
        special: true,
        metaRows: [],
        yearValue: '',
        valueHeaders: {
          value1: 'Значение 1',
          value2: 'Значение 2',
        },
        title: '',
        formTitle: '',
        note,
        headerRowCount: 2,
        layoutVariant,
        tableRows: trimmed,
      }
    },
    /** Строка из Excel только с подписью «Закупки» (не заголовок раздела) — ломает colspan, убираем. */
    isProcurementLeadingLabelRow(row) {
      const parts = (row || [])
        .map(c => String(c?.value ?? '').trim())
        .filter(Boolean)
      if (!parts.length) return false
      // В merged-диапазоне ExcelJS дублирует «Закупки» в каждой ячейке строки.
      return parts.every(p => /^закупки$/i.test(p))
    },
    stripProcurementLeadingLabelRows(tableRows) {
      let out = tableRows || []
      while (out.length && goPricingLayoutEngine.isProcurementLeadingLabelRow(out[0])) {
        out = out.slice(1)
      }
      return out
    },
    /** Строка-заголовок листа «Закупки… ИАС Закупки» — всё до неё и сама строка не показываются в таблице. */
    layoutProcurement(rows) {
      const parsed = rows.map((row, idx) => ({
        idx,
        values: goPricingLayoutEngine.rowValues(row),
      }))
      let note = ''
      let tableEnd = rows.length
      const noteStartProc = goPricingLayoutEngine.findFirstNoteSectionRowIndex(rows, 0)
      if (noteStartProc >= 0) {
        tableEnd = noteStartProc
        note = goPricingLayoutEngine.collectNoteRows(rows, noteStartProc)
      }

      const titleMatch = values => {
        const s = values.join(' ').toLowerCase()
        return (
          s.includes('закупки предлагаемого') ||
          s.includes('иас закупки') ||
          (s.includes('информационн') &&
            s.includes('ресурс') &&
            s.includes('закупк'))
        )
      }

      const titleRow = parsed.find(
        r => r.idx < tableEnd && titleMatch(r.values)
      )
      let startIdx = 0
      if (titleRow) {
        startIdx = titleRow.idx + 1
      } else {
        while (startIdx < tableEnd) {
          const vals = goPricingLayoutEngine.rowValues(rows[startIdx])
          if (!vals.length) {
            startIdx++
            continue
          }
          if (/^(МНН|ЛФ|ФВ)$/i.test(vals[0])) {
            startIdx++
            continue
          }
          if (vals.some(v => /^значение\s*1$/i.test(v))) {
            startIdx++
            continue
          }
          if (/^год$/i.test(vals[0]) && vals.length <= 2) {
            startIdx++
            continue
          }
          break
        }
      }

      while (startIdx < tableEnd && !goPricingLayoutEngine.rowValues(rows[startIdx]).length) {
        startIdx++
      }

      const sliced = goPricingLayoutEngine.stripProcurementLeadingLabelRows(
        rows.slice(startIdx, tableEnd)
      )
      const prepared = goPricingLayoutEngine.mergeHorizontallyAdjacentIdentical(
        goPricingLayoutEngine.mergeVerticallyAdjacentIdentical(goPricingLayoutEngine.ensureCellMergeProps(sliced))
      )
      const mergedStripped = goPricingLayoutEngine.stripProcurementLeadingLabelRows(
        prepared
      )
      const { tableRows, note: noteOut } = goPricingLayoutEngine.stripTrailingNoteRowsFromTable(
        mergedStripped,
        note
      )
      return goPricingLayoutEngine.emptyProcurementLayout(tableRows, noteOut, {
        layoutVariant: 'procurement',
      })
    },
    /** Лист «Ценовая характеристика»: всё до строки с заголовком не показываем; merge как у «Закупки». */
    layoutPricing(rows) {
      const parsed = rows.map((row, idx) => ({
        idx,
        values: goPricingLayoutEngine.rowValues(row),
      }))
      let note = ''
      let tableEnd = rows.length
      const noteStartPrice = goPricingLayoutEngine.findFirstNoteSectionRowIndex(rows, 0)
      if (noteStartPrice >= 0) {
        tableEnd = noteStartPrice
        note = goPricingLayoutEngine.collectNoteRows(rows, noteStartPrice)
      }

      const titleMatch = values => {
        const s = values.join(' ').toLowerCase()
        return s.includes('ценовая характеристика')
      }

      const titleRow = parsed.find(
        r => r.idx < tableEnd && titleMatch(r.values)
      )
      let startIdx = 0
      if (titleRow) {
        startIdx = titleRow.idx + 1
      } else {
        while (startIdx < tableEnd) {
          const vals = goPricingLayoutEngine.rowValues(rows[startIdx])
          if (!vals.length) {
            startIdx++
            continue
          }
          if (/^(МНН|ЛФ|ФВ)$/i.test(vals[0])) {
            startIdx++
            continue
          }
          if (vals.some(v => /^значение\s*1$/i.test(v))) {
            startIdx++
            continue
          }
          if (/^год$/i.test(vals[0]) && vals.length <= 2) {
            startIdx++
            continue
          }
          break
        }
      }

      while (startIdx < tableEnd && !goPricingLayoutEngine.rowValues(rows[startIdx]).length) {
        startIdx++
      }

      const sliced = rows.slice(startIdx, tableEnd)
      const cells = goPricingLayoutEngine.ensureCellMergeProps(sliced)
      const retailColIdx =
        goPricingLayoutEngine.findPricingRetailPriceColumnIndex(cells)
      const prepared = goPricingLayoutEngine.mergeHorizontallyAdjacentIdentical(
        goPricingLayoutEngine.mergeVerticallyAdjacentIdentical(cells, {
          numericMergeColumns:
            retailColIdx >= 0 ? [retailColIdx] : [],
        })
      )
      const { tableRows, note: noteOut } = goPricingLayoutEngine.stripTrailingNoteRowsFromTable(
        prepared,
        note
      )
      return goPricingLayoutEngine.emptyProcurementLayout(
        goPricingLayoutEngine.stripEmptyPricingPlanRegColumn(tableRows),
        noteOut,
        { stripLeading: true, layoutVariant: 'pricing' }
      )
    },
  findPricingRetailPriceColumnIndex(tableRows) {
      const rows = tableRows || []
      const maxScan = Math.min(rows.length, 10)
      for (let r = 0; r < maxScan; r++) {
        const row = rows[r] || []
        for (let c = 0; c < row.length; c++) {
          const t = String(row[c]?.value || '')
          if (/розничн/i.test(t)) return c
        }
      }
      const headerRow = rows[0] || []
      return headerRow.length > 1 ? headerRow.length - 1 : -1
    },
    findPricingPlanRegistrationColumnIndex(tableRows) {
      const rows = tableRows || []
      const maxScan = Math.min(rows.length, 10)
      for (let r = 0; r < maxScan; r++) {
        const row = rows[r] || []
        for (let c = 0; c < row.length; c++) {
          const t = String(row[c]?.value || '')
          if (/план\.?\s*к\s*рег-ци/i.test(t)) return c
        }
      }
      for (let r = 0; r < maxScan; r++) {
        const row = rows[r] || []
        for (let c = 0; c < row.length; c++) {
          const t = String(row[c]?.value || '')
          if (/план/i.test(t) && /рег-ци/i.test(t)) return c
        }
      }
      return -1
    },
    pricingPlanRegCellIsBlankLike(value) {
      const s = String(value ?? '').trim()
      if (!s) return true
      if (s === '-' || s === '~') return true
      return false
    },
    /** У «Ценовая характеристика» убираем столбец плана к регистрации, если там нет данных. */
    stripEmptyPricingPlanRegColumn(tableRows) {
      const rows = tableRows || []
      if (!rows.length) return rows
      const colIdx = goPricingLayoutEngine.findPricingPlanRegistrationColumnIndex(rows)
      if (colIdx < 0) return rows

      let hasData = false
      for (const row of rows) {
        const cell = (row || [])[colIdx]
        if (!cell || cell.hidden) continue
        const v = String(cell.value ?? '').trim()
        if (goPricingLayoutEngine.pricingPlanRegCellIsBlankLike(v)) continue
        if (/^план/i.test(v) && /рег-ци/i.test(v)) continue
        hasData = true
        break
      }
      if (hasData) return rows
      return rows.map(row => (row || []).filter((_, i) => i !== colIdx))
    },
    /** Поля для rowspan / colspan / hidden. */
    ensureCellMergeProps(tableRows) {
      return (tableRows || []).map(row =>
        goPricingLayoutEngine.trimRowRightEmptyCells(
          (row || []).map(cell => ({
            value: String(cell?.value ?? '').trim(),
            merged: cell?.merged || false,
            mergeObj: cell?.mergeObj || null,
            hidden: false,
            rowspan: 1,
            colspan: 1,
          }))
        )
      )
    },
    trimRowRightEmptyCells(row) {
      const out = [...(row || [])]
      while (out.length) {
        const last = out[out.length - 1]
        const val = String(last?.value ?? '').trim()
        if (val) break
        out.pop()
      }
      return out
    },
    normalizeMergeValue(value) {
      return String(value ?? '').trim().replace(/\s/g, '')
    },
    cellValueIsNumericLike(value) {
      return /^-?\d+(?:[.,]\d+)?$/.test(
        goPricingLayoutEngine.normalizeMergeValue(value)
      )
    },
    mergeValuesEqual(a, b, allowNumeric) {
      const left = String(a ?? '').trim()
      const right = String(b ?? '').trim()
      if (!allowNumeric) return left === right
      if (
        goPricingLayoutEngine.cellValueIsNumericLike(left) &&
        goPricingLayoutEngine.cellValueIsNumericLike(right)
      ) {
        return (
          goPricingLayoutEngine.normalizeMergeValue(left) ===
          goPricingLayoutEngine.normalizeMergeValue(right)
        )
      }
      return left === right
    },
    /** Не сливать розничную цену через границу формы выпуска. */
    blocksNumericVerticalMerge(rows, startRow, candidateRow) {
      if (candidateRow <= startRow) return false
      const firstCol = rows[candidateRow]?.[0]
      if (!firstCol || firstCol.hidden) return false
      return !!String(firstCol.value ?? '').trim()
    },
    /** Одинаковые непустые значения подряд в столбце — rowspan. */
    mergeVerticallyAdjacentIdentical(tableRows, { numericMergeColumns = [] } = {}) {
      const rows = (tableRows || []).map(row => [...(row || [])])
      if (!rows.length) return rows
      const numericMergeSet = new Set(numericMergeColumns)
      const maxCol = Math.max(0, ...rows.map(r => r.length))
      for (let c = 0; c < maxCol; c++) {
        const allowNumeric = numericMergeSet.has(c)
        let i = 0
        while (i < rows.length) {
          const cell = rows[i][c]
          if (!cell || cell.hidden) {
            i++
            continue
          }
          const val = String(cell.value ?? '').trim()
          if (val === '') {
            i++
            continue
          }
          if (goPricingLayoutEngine.cellValueIsNumericLike(val) && !allowNumeric) {
            i++
            continue
          }
          let j = i + 1
          while (j < rows.length) {
            const next = rows[j][c]
            if (!next || next.hidden) break
            if (
              allowNumeric &&
              goPricingLayoutEngine.blocksNumericVerticalMerge(rows, i, j)
            ) {
              break
            }
            const nextVal = String(next.value ?? '').trim()
            if (!goPricingLayoutEngine.mergeValuesEqual(val, nextVal, allowNumeric)) {
              break
            }
            j++
          }
          const span = j - i
          if (span > 1) {
            cell.rowspan = span
            for (let k = i + 1; k < j; k++) {
              if (rows[k][c]) rows[k][c].hidden = true
            }
          }
          i = j
        }
      }
      return rows
    },
    /** Одинаковые непустые значения подряд в строке — colspan. */
    mergeHorizontallyAdjacentIdentical(tableRows) {
      const rows = tableRows
      const numericLike = v => /^-?\d+(?:[.,]\d+)?$/.test(String(v ?? '').trim())
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r]
        if (!row || !row.length) continue
        let c = 0
        while (c < row.length) {
          const cell = row[c]
          if (!cell || cell.hidden) {
            c++
            continue
          }
          const val = String(cell.value ?? '').trim()
          if (val === '') {
            c++
            continue
          }
          if (numericLike(val)) {
            c++
            continue
          }
          let span = 1
          let cc = c + 1
          while (cc < row.length) {
            const next = row[cc]
            if (!next || next.hidden) break
            const nextVal = String(next.value ?? '').trim()
            if (nextVal !== val) break
            span++
            cc++
          }
          if (span > 1) {
            cell.colspan = span
            for (let k = c + 1; k < c + span; k++) {
              if (row[k]) row[k].hidden = true
            }
          }
          c += span
        }
      }
      return rows
    },
    /**
     * Первая строка блока примечаний: склейка всех ячеек (и слитно, и через пробел),
     * чтобы ловить «Примечание», «Примечания» и разбивку по ячейкам.
     */
    findFirstNoteSectionRowIndex(rows, fromIdx = 0) {
      const from = Math.max(0, Number(fromIdx) || 0)
      for (let i = from; i < rows.length; i++) {
        const cells = rows[i] || []
        const parts = cells.map(c => String(c?.value ?? '').trim())
        const tight = parts.join('').toLowerCase()
        const spaced = parts.filter(Boolean).join(' ').toLowerCase()
        if (tight.includes('примечани') || spaced.includes('примечани')) return i
      }
      return -1
    },
    /** Если строка с примечанием попала в данные после merge — убрать снизу и дописать к note. */
    stripTrailingNoteRowsFromTable(tableRows, existingNote) {
      let out = (tableRows || []).map(r => [...(r || [])])
      const tailLines = []
      while (out.length) {
        const last = out[out.length - 1]
        const parts = (last || []).map(c => String(c?.value ?? '').trim())
        const tight = parts.join('').toLowerCase()
        const spaced = parts.filter(Boolean).join(' ').toLowerCase()
        if (!tight.includes('примечани') && !spaced.includes('примечани')) break
        const line = parts.filter(Boolean).join(' ').trim()
        if (line) tailLines.unshift(line)
        out = out.slice(0, -1)
      }
      const tailNote = tailLines.join('\n\n').trim()
      const note = [existingNote, tailNote].filter(Boolean).join('\n\n').trim()
      return { tableRows: out, note }
    },
    /** Все строки с первой «Примечания» до конца листа — один блок примечаний. */
    collectNoteRows(rows, startIdx) {
      const from = Math.max(0, Number(startIdx) || 0)
      const lines = []
      for (let r = from; r < rows.length; r++) {
        const line = (rows[r] || [])
          .map(c => String(c?.value || '').trim())
          .filter(Boolean)
          .join(' ')
          .trim()
        if (line) lines.push(line)
      }
      return lines.join('\n\n').trim()
    },
    rowValues(row) {
      return (row || [])
        .map(cell => String(cell?.value ?? '').trim())
        .filter(Boolean)
    },
    rowValuesWithGaps(row) {
      const vals = (row || []).map(cell => String(cell?.value ?? '').trim())
      while (vals.length && !vals[vals.length - 1]) vals.pop()
      return vals
    },
    /** Без сжатия столбцов — иначе пустые ячейки в Excel сдвигают годы относительно чисел. */
    rowCellsTrimmed(row) {
      return (row || []).map(cell => String(cell?.value ?? '').trim())
    },
    /** Строка-заголовок «форма выпуска / годы» и индексы столбцов годов. */
    parseGoReleaseYearHeader(compact, yearRegex) {
      const rowLooksLikeYearHeader = vals => {
        if (!vals.some(Boolean)) return false
        const joined = vals.filter(Boolean).join(' ').toLowerCase()
        if (joined.includes('примечани')) return false
        const yearIdxs = vals
          .map((v, j) => (yearRegex.test(String(v).trim()) ? j : -1))
          .filter(j => j >= 0)
        if (!yearIdxs.length) return false
        const first = String(vals[0] || '')
        if (/форма выпуска/i.test(first) || /^год$/i.test(first)) return true
        const onlyYears = vals.every((v, j) => {
          if (!v) return true
          return yearRegex.test(v)
        })
        return onlyYears && yearIdxs.length >= 2
      }

      let headerIdx = compact.findIndex(rowLooksLikeYearHeader)
      let excludeHeaderRowFromBody = headerIdx >= 0
      if (headerIdx < 0) {
        headerIdx = compact.findIndex(
          vals => vals.filter(v => yearRegex.test(String(v).trim())).length >= 2
        )
        if (headerIdx >= 0) excludeHeaderRowFromBody = true
      }
      if (headerIdx < 0) headerIdx = 0

      const headerVals = compact[headerIdx] || []
      let firstYearCol = headerVals.findIndex(v => yearRegex.test(String(v).trim()))
      if (firstYearCol < 0) firstYearCol = 1

      const years = []
      const yearCols = []
      let lastNorm = null
      for (let j = firstYearCol; j < headerVals.length; j++) {
        const v = String(headerVals[j] || '').trim()
        if (!yearRegex.test(v)) continue
        const normalized = /\s*год$/i.test(v) ? v : `${v} год`
        if (normalized === lastNorm) continue
        years.push(normalized)
        yearCols.push(j)
        lastNorm = normalized
      }

      return { headerIdx, firstYearCol, years, yearCols, excludeHeaderRowFromBody }
    },
    normalizeGoTable(rows, tableStart, tableEnd) {
      const compact = rows
        .slice(tableStart, tableEnd)
        .map(row => goPricingLayoutEngine.rowCellsTrimmed(row))
        .filter(values => values.some(Boolean))
      if (!compact.length) return []

      const yearRegex = /^\d{4}(?:\s*год)?$/i
      const numRegex = /^-?\d+(?:[.,]\d+)?$/

      const { headerIdx, firstYearCol, years, yearCols, excludeHeaderRowFromBody } =
        goPricingLayoutEngine.parseGoReleaseYearHeader(compact, yearRegex)

      let yearsOrdered = years
      let yearColumns = yearCols
      if (!yearsOrdered.length) {
        yearsOrdered = []
        yearColumns = []
        for (const vals of compact) {
          for (let i = 0; i < vals.length; i++) {
            const v = vals[i]
            if (!yearRegex.test(String(v).trim())) continue
            const normalized = /\s*год$/i.test(v) ? v : `${v} год`
            if (yearsOrdered.includes(normalized)) continue
            yearsOrdered.push(normalized)
            yearColumns.push(i)
          }
        }
      }
      if (!yearsOrdered.length) return []

      const headerValsForBody = compact[headerIdx] || []
      const headerFirstIsYear = yearRegex.test(
        String(headerValsForBody[0] || '').trim()
      )
      const needsBodyShift = headerFirstIsYear && firstYearCol === 0

      const bodyRows = compact.filter((vals, idx) => {
        if (excludeHeaderRowFromBody && idx === headerIdx) return false
        if (!vals.some(Boolean)) return false
        if (vals.some(v => String(v).toLowerCase().includes('примечани'))) return false
        const first = String(vals.find(v => String(v || '').trim()) || '')
        if (/^год$/i.test(first) || /^форма выпуска$/i.test(first)) return false
        if (/^выпуск в гражданский оборот/i.test(vals.join(' '))) return false
        return vals.some(v => numRegex.test(v))
      })

      const out = [goPricingLayoutEngine.makeCells(['Год/ форма выпуска', ...yearsOrdered])]

      for (const vals of bodyRows) {
        const rowShift =
          needsBodyShift &&
          !numRegex.test(String(vals[0] || '').trim()) &&
          String(vals[0] || '').trim() !== ''
            ? 1
            : 0
        const bodyYearStart = firstYearCol + rowShift
        const nameParts = vals
          .slice(0, bodyYearStart)
          .map(v => String(v || '').trim())
          .filter(Boolean)
        const name = nameParts.join(' ')
        const right = []
        for (let j = 0; j < yearsOrdered.length; j++) {
          const startCol = (yearColumns[j] ?? (firstYearCol + j)) + rowShift
          const endColExclusive =
            j + 1 < yearsOrdered.length
              ? (yearColumns[j + 1] ?? (firstYearCol + j + 1)) + rowShift
              : vals.length
          let picked = ''
          for (let c = startCol; c < Math.max(startCol + 1, endColExclusive); c++) {
            const s = String(vals[c] ?? '').trim()
            if (numRegex.test(s)) {
              picked = s
              break
            }
          }
          right.push(picked)
        }
        out.push(goPricingLayoutEngine.makeCells([name, ...right]))
      }
      return out
    },
    normalizePlanSupplyTable(rows) {
      const parsed = rows.map((row, idx) => ({
        idx,
        values: goPricingLayoutEngine.rowValues(row),
        rawValues: goPricingLayoutEngine.rowValuesWithGaps(row),
      }))
      const headerIdx = parsed.findIndex(r =>
        r.values.some(v => /^месяц$/i.test(v)) &&
        r.values.some(v => /^план поставок/i.test(v))
      )
      if (headerIdx < 0) return { title: '', note: '', tableRows: [] }

      let title = ''
      let note = ''
      let tableEnd = rows.length

      for (const r of parsed) {
        const values = r.values
        if (!values.length) continue
        if (!title && values.some(v => /^план поставок/i.test(v))) {
          title = values.join(' ').trim()
          continue
        }
      }

      const noteStartPlan = goPricingLayoutEngine.findFirstNoteSectionRowIndex(rows, 0)
      if (noteStartPlan >= 0) {
        tableEnd = noteStartPlan
        note = goPricingLayoutEngine.collectNoteRows(rows, noteStartPlan)
      }

      const formName = goPricingLayoutEngine.pickPlanFormTitle(parsed, headerIdx)

      const headerRow = parsed[headerIdx]?.rawValues || []
      const yearsRow = headerIdx > 0 ? parsed[headerIdx - 1]?.rawValues || [] : []
      const indexOfMatch = (arr, re) => arr.findIndex(v => re.test(String(v || '').trim()))

      const monthCol = indexOfMatch(headerRow, /^месяц$/i)
      const planCol = indexOfMatch(headerRow, /^план поставок/i)
      let releaseCol = indexOfMatch(headerRow, /^выпуск.*гражданск/i)
      let yearCol = indexOfMatch(headerRow, /^год$/i)
      if (yearCol < 0) {
        yearCol = indexOfMatch(yearsRow, /^год$/i)
      }
      if (yearCol < 0) yearCol = 0
      if (monthCol < 0 || planCol < 0) {
        return { title, note, tableRows: [] }
      }
      if (releaseCol < 0) {
        // В ряде шаблонов заголовок 4-й колонки "съедается" merged-ячейкой.
        // Тогда считаем, что "Выпуск в ГО" идет сразу после "План поставок".
        releaseCol = Math.max(planCol + 1, 3)
      }

      const tableRows = [
        goPricingLayoutEngine.makeCells([
          'Год',
          'Месяц',
          'План поставок',
          'Выпуск в гражданский оборот',
        ]),
      ]

      const body = parsed.filter(r => r.idx > headerIdx && r.idx < tableEnd)
      for (const r of body) {
        const vals = r.rawValues
        const month = vals[monthCol] || ''
        const plan = vals[planCol] || ''
        const release = vals[releaseCol] || ''
        const year = vals[yearCol] || ''
        if (!month && !plan && !release && !year) continue
        if (goPricingLayoutEngine.isPlanYearSeparatorValues(year, month, plan, release)) {
          const label =
            [year, month, plan, release].map(v => String(v || '').trim()).find(v => v) || ''
          tableRows.push(goPricingLayoutEngine.makePlanYearSeparatorRow(label))
          continue
        }
        tableRows.push(goPricingLayoutEngine.makeCells([year, month, plan, release]))
      }

      return {
        title,
        formTitle: formName,
        note,
        tableRows,
      }
    },
    /** Убираем столбец «Год» — год уже виден в группирующих строках. */
    stripPlanTableYearColumn(tableRows) {
      return (tableRows || []).map(row => (row || []).slice(1))
    },
    normalizeFormTitle(line) {
      const src = String(line || '').trim()
      if (!src) return ''
      const m = src.match(/^(.+?)(?:\s+\1)+$/)
      return (m ? m[1] : src).trim()
    },
    isPlanYearLabel(text) {
      return /^\d{4}(?:\s*год)?$/i.test(String(text || '').trim())
    },
    pickPlanFormTitle(parsed, headerIdx) {
      let formName = ''
      for (let i = 0; i < headerIdx; i++) {
        const values = parsed[i]?.values || []
        if (!values.length) continue
        const line = values.join(' ').trim()
        if (!line) continue
        if (/^план поставок/i.test(line)) continue
        if (/^год$/i.test(line)) continue
        if (/^значение\s*\d+/i.test(line)) continue
        if (/^(МНН|ЛФ|ФВ)$/i.test(values[0])) continue
        if (goPricingLayoutEngine.isPlanYearLabel(line)) continue
        if (values.every(v => !String(v || '').trim() || goPricingLayoutEngine.isPlanYearLabel(v))) {
          continue
        }
        formName = goPricingLayoutEngine.normalizeFormTitle(line)
      }
      return formName
    },
    isPlanYearSeparatorValues(year, month, plan, release) {
      const parts = [year, month, plan, release].map(v => String(v || '').trim())
      const label = parts.find(v => v && goPricingLayoutEngine.isPlanYearLabel(v))
      if (!label) return false
      return parts.every(v => !v || v === label)
    },
    makePlanYearSeparatorRow(label) {
      const yearLabel = String(label || '').trim()
      return goPricingLayoutEngine.makeCells(['', yearLabel, yearLabel, yearLabel])
    },
    groupYearCells(tableRows) {
      const rows = (tableRows || []).map(row =>
        (row || []).map(cell => ({
          ...cell,
          hidden: false,
          rowspan: 1,
          colspan: cell.colspan || 1,
        }))
      )
      if (rows.length < 3) return rows

      let i = 1 // первая строка в tableRows — заголовок
      while (i < rows.length) {
        const year = String(rows[i]?.[0]?.value || '').trim()
        if (!year) {
          i++
          continue
        }
        let j = i + 1
        while (j < rows.length) {
          const next = String(rows[j]?.[0]?.value || '').trim()
          if (next !== year) break
          j++
        }
        const span = j - i
        rows[i][0].rowspan = span
        for (let k = i + 1; k < j; k++) {
          rows[k][0].hidden = true
        }
        i = j
      }

      // Для строк-разделителей года в таблице "текущий год":
      // [пусто, 2025 год, 2025 год, 2025 год] => одна ячейка на 3 колонки.
      const yearRegex = /^\d{4}(?:\s*год)?$/i
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r]
        if (!row || row.length < 4) continue
        if (row[0]?.hidden) continue
        const c0 = String(row[0]?.value || '').trim()
        const c1 = String(row[1]?.value || '').trim()
        const c2 = String(row[2]?.value || '').trim()
        const c3 = String(row[3]?.value || '').trim()
        if (c0 || !c1 || c1 !== c2 || c1 !== c3 || !yearRegex.test(c1)) {
          if (!yearRegex.test(c1) || c1 !== c2 || c1 !== c3) continue
          if (c0 && c0 !== c1) continue
          if (c0 === c1) {
            row[0].colspan = 4
            row[1].hidden = true
            row[2].hidden = true
            row[3].hidden = true
          }
          continue
        }
        row[1].colspan = 3
        row[2].hidden = true
        row[3].hidden = true
      }
      return rows
    },
    makeCells(values) {
      return values.map(value => ({
        value: String(value ?? ''),
        merged: false,
        mergeObj: null,
        hidden: false,
        rowspan: 1,
        colspan: 1,
      }))
    },
        /** Пробелы между разрядами: 122432 → 122 432 */
    formatIntegerWithSpaces(intStr) {
      const s = String(intStr ?? '').trim()
      const neg = s.startsWith('-')
      const d = neg ? s.slice(1) : s
      if (!/^\d+$/.test(d)) return s
      const grouped = d.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return neg ? `-${grouped}` : grouped
    },
    formatCellValue(value) {
      const raw = String(value ?? '').trim()
      if (!raw) return ''
      // Десятичные: запятая, 2 знака после, разряды в целой части.
      let m = raw.match(/^(-?\d+)[.,](\d+)$/)
      if (m) {
        const intPart = goPricingLayoutEngine.formatIntegerWithSpaces(m[1])
        const decPart = m[2].slice(0, 2).padEnd(2, '0')
        return `${intPart},${decPart}`
      }
      // Целые числа
      m = raw.match(/^(-?\d+)$/)
      if (m) return goPricingLayoutEngine.formatIntegerWithSpaces(m[1])
      return raw
    },
};

export function resolveGoPricingLayout(rows, variant = 'default') {
  return goPricingLayoutEngine.layout(rows, variant);
}
